"use client";

import { useMemo } from "react";
import { Lock } from "lucide-react";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import { useCourseProgress } from "@/features/progress/hooks";
import type { CurriculumCourse } from "../../lib/curriculum-props";
import type { Locale } from "@/lib/i18n";
import type { EntitlementAccessScope } from "@/features/dashboard/types";
import { hasFullAccessEntitlement } from "../../lib/resolve-course-plan-owned-state";
import { CourseCurriculumSection } from "./CourseCurriculumSection";
import { CourseShowcaseSectionHeader } from "./CourseShowcaseSectionHeader";

type Props = {
  course: CurriculumCourse;
  locale: Locale;
  /**
   * When true (enrolled_only visibility mode): non-owners and logged-out
   * visitors see the locked message instead of the curriculum with lock icons.
   * Owned users see the full curriculum as usual.
   */
  enrolledOnlyMode?: boolean;
  headerEyebrow?: string;
};

/**
 * Sentinel scope for the `owned-legacy-full` state. Semantically equivalent
 * to `undefined` for the current `resolveCurriculumLessonRowModel`
 * implementation (both paths yield `owned_open`), but materializing the
 * scope here documents the intent and is defensive: if a future backend or
 * migration regression accidentally sends a narrow `accessScope` on a
 * legacy row, the gate still displays zero `<Lock />` icons to the learner.
 */
const LEGACY_FULL_ACCESS_SCOPE: EntitlementAccessScope = {
  type: "full",
  phaseIds: [],
  moduleIds: [],
};

function CourseContentLockedBanner({
  locale,
  headerEyebrow,
}: {
  locale: Locale;
  headerEyebrow?: string;
}) {
  const message =
    locale === "bn"
      ? "কোর্স কনটেন্ট, ক্লাস লিংক, রেকর্ডিং এবং রিসোর্স এনরোলমেন্টের পরে দেখা যাবে। জয়েন করার আগে উপরের শেখার যাত্রায় পুরো কাঠামো দেখা যাবে।"
      : "Course content, class links, recordings, and resources are available after enrollment. The Learning Journey above shows the planned structure before you join.";

  const title = locale === "bn" ? "কোর্স কন্টেন্ট" : "Course Content";

  return (
    <section>
      <CourseShowcaseSectionHeader eyebrow={headerEyebrow} title={title} />
      <p className="flex items-start gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-4 text-[14px] leading-[1.6] text-neutral-700">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
        {message}
      </p>
    </section>
  );
}

export function CourseCurriculumGate({
  course,
  locale,
  enrolledOnlyMode = false,
  headerEyebrow,
}: Props) {
  const { getToken } = useAuth();
  const token = getToken() ?? undefined;
  const { data: library, isPending: isLibraryPending } = useLibrary(token);
  const entry = useMemo(
    () => library?.courses.find((c) => c.courseId === course._id),
    [library, course._id],
  );
  const isOwned = Boolean(entry);
  const shouldLoadProgress = isOwned && Boolean(token) && Boolean(course._id);
  const { data: progressData } = useCourseProgress(
    shouldLoadProgress ? course._id : "",
    token,
  );
  const completedLessonIds = useMemo(
    () => progressData?.items.map((i) => i.lessonId) ?? [],
    [progressData],
  );

  const accessScope = hasFullAccessEntitlement(entry)
    ? LEGACY_FULL_ACCESS_SCOPE
    : entry?.accessScope;

  // enrolled_only mode: non-owners see the locked banner; owners see the full
  // curriculum. While the library is still fetching (token present, not yet
  // resolved), show a skeleton to avoid a flash of the locked state for
  // enrolled users.
  if (enrolledOnlyMode && !isOwned) {
    if (token && isLibraryPending) {
      return (
        <div className="mt-12 h-64 animate-pulse rounded-xl bg-neutral-100" />
      );
    }
    return <CourseContentLockedBanner locale={locale} headerEyebrow={headerEyebrow} />;
  }

  return (
    <CourseCurriculumSection
      modules={course.modules}
      courseSlug={course.slug}
      locale={locale}
      isOwned={isOwned}
      ownedAccessScope={accessScope}
      completedLessonIds={completedLessonIds}
      coursePhases={course.phases}
      headerEyebrow={headerEyebrow}
      enrollmentGrantedAt={
        entry?.grantedAt ? new Date(entry.grantedAt) : null
      }
      nextCohortStartAt={course.nextCohortStartAt ?? null}
    />
  );
}
